import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Stack,
  Text,
} from "@chakra-ui/react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import { ChildDeletionBlocker } from "../../types";

const CART_MESSAGE =
  "Nie możesz usunąć profilu, ponieważ jest przypisany do produktu w koszyku. Usuń produkt z koszyka, aby kontynuować.";
const WAITING_MESSAGE =
  "Nie możesz usunąć profilu, ponieważ jest przypisany do zamówienia oczekującego na płatność.";
const CLOSED_MESSAGE =
  "Nie możesz usunąć profilu, ponieważ jest przypisany do wcześniejszego zamówienia. Profil jest zachowany dla poprawności historii zakupów.";
const UNKNOWN_MESSAGE =
  "Nie możemy teraz potwierdzić, czy profil jest powiązany z zamówieniami. Spróbuj ponownie później lub skontaktuj się z biurem.";

type Reason = "cart" | "waiting" | "closed";

const reasonFor = (blocker: ChildDeletionBlocker): Reason => {
  if (blocker === ChildDeletionBlocker.OpenCart) return "cart";
  if (blocker === ChildDeletionBlocker.OrderWaitingForPayment) return "waiting";
  // Purchase history without a matching order still means a past purchase
  return "closed";
};

const links: Record<Reason, { label: string; path: string; message: string }> =
  {
    cart: { label: "Przejdź do koszyka", path: "/cart", message: CART_MESSAGE },
    waiting: {
      label: "Zamówienia w trakcie opłacania",
      path: "/active",
      message: WAITING_MESSAGE,
    },
    closed: {
      label: "Historia zamówień",
      path: "/archive",
      message: CLOSED_MESSAGE,
    },
  };

/**
 * Explains why a saved child profile cannot be deleted (SCRUM-134). Informational only — the
 * server refuses the deletion on its own. `blockers` is null/undefined when the check failed.
 */
export default function ChildDeletionInfo({
  blockers,
}: {
  blockers?: ChildDeletionBlocker[] | null;
}) {
  const navigate = useNavigate();

  if (!blockers) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription fontSize="sm">{UNKNOWN_MESSAGE}</AlertDescription>
      </Alert>
    );
  }
  const reasons = _.uniq(blockers.map(reasonFor));
  if (reasons.length === 0) return null;

  const navigationLinks = reasons.filter(
    (reason) => reason !== "closed" || reasons.length > 1
  );
  return (
    <Alert status="info" borderRadius="md" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing={2}>
        {reasons.length === 1 ? (
          <AlertDescription fontSize="sm">
            {links[reasons[0]].message}
          </AlertDescription>
        ) : (
          <>
            <AlertDescription fontSize="sm">
              Nie możesz usunąć profilu, ponieważ jest przypisany do produktów
              lub zamówień:
            </AlertDescription>
            {reasons.map((reason) => (
              <Text key={reason} fontSize="sm">
                •{" "}
                {links[reason].message.replace(
                  "Nie możesz usunąć profilu, ponieważ jest p",
                  "P"
                )}
              </Text>
            ))}
          </>
        )}
        {navigationLinks.length > 0 && (
          <Stack direction="row" wrap="wrap" spacing={2}>
            {navigationLinks.map((reason) => (
              <Button
                key={reason}
                size="xs"
                onClick={() => navigate(links[reason].path)}
              >
                {links[reason].label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
